import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import {
  Button,
  CircularProgress,
  PageSidebar,
  SnackbarList,
  useSnackbarContext,
} from "@inductiveautomation/ws-components";
import { useEffect, useRef, useState } from "react";
import {
  usePutAdditionalEmailsVerifyMutation,
  usePutResendAdditionalEmailsVerifyMutation,
} from "../api/profile.api";
import { getConfirmationStyles } from "./_confirmation.styles";
import { useCurrentUser } from "../../../hooks/currentUser.ts";
import { handleResponse } from "../../../utilities/api_response.ts";

const AdditionalEmailVerification = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "profile.confirmation.additional_email",
  });
  const { t: profileT } = useTranslation("translation", {
    keyPrefix: "profile",
  });
  // Makes Auth0 token available in store since this page does not use the root component
  const { token: auth0Token } = useCurrentUser();
  const { snackbars, removeSnackbar, addSnackbar } = useSnackbarContext();

  const { classes } = getConfirmationStyles();

  const [searchParams] = useSearchParams();
  const [verifyAdditionalEmail] = usePutAdditionalEmailsVerifyMutation();
  const [resendAdditionalEmailVerification] =
    usePutResendAdditionalEmailsVerifyMutation();

  const token = searchParams.get("$token");
  const email = searchParams.get("email");

  const [requestState, setRequestState] = useState<
    "loading" | "success" | "error"
  >("loading");
  const verificationRequested = useRef(false);

  useEffect(() => {
    if (!token || !email) {
      setRequestState("error");
      return;
    }
    if (verificationRequested.current || !auth0Token) return;

    // ensures that verification only runs once, as it will fail on subsequent calls
    verificationRequested.current = true;
    verifyAdditionalEmail({
      token: token,
      email: email,
    })
      .then((res) => {
        if (res.data) {
          setRequestState("success");
          window.history.pushState({}, "", "/confirm/additional-email");
        } else {
          setRequestState("error");
        }
      })
      .catch((error: unknown) => {
        setRequestState("error");
        console.error(error);
      });
  }, [email, token, verifyAdditionalEmail, auth0Token]);

  const resendVerification = () => {
    if (email) {
      resendAdditionalEmailVerification({ email })
        .then((response) => {
          handleResponse(response, {
            onSuccess: () => {
              addSnackbar(profileT("additional_emails.resend_success_message"));
            },
            onError: () => {
              addSnackbar(profileT("additional_emails.resend_error_message"), {
                type: "error",
              });
            },
          });
        })
        .catch(() => {
          addSnackbar(profileT("additional_emails.resend_error_message"), {
            type: "error",
          });
        });
    } else {
      addSnackbar(profileT("additional_emails.resend_error_message"), {
        type: "error",
      });
    }
  };

  return (
    <PageSidebar
      testId="additional-email-verification"
      heading={
        requestState === "loading" ? (
          <span className={classes.headingWithSpinner}>
            {t("loading.heading")}
            <CircularProgress
              size="small"
              testId="verifying-email-spinner"
            />
          </span>
        ) : (
          t(`${requestState}.heading`)
        )
      }
      supportingText={t(`${requestState}.description`)}
    >
      <SnackbarList snackbars={snackbars} removeSnackbar={removeSnackbar} />
      <div className={classes.helperContainer}>
        <div className={classes.helperContainer}>
          <span className={classes.helperText}>
            {requestState === "success" && (
              <Button id="go-to-account" href="/" buttonVariant={"primary"}>
                {t(`success.button_text`)}
              </Button>
            )}
            {requestState === "error" && (
              <Button
                id="resend-verification"
                buttonVariant={"primary"}
                onClick={resendVerification}
              >
                {t(`error.button_text`)}
              </Button>
            )}
          </span>
        </div>
      </div>
    </PageSidebar>
  );
};

export default AdditionalEmailVerification;
